import type { ColumnLanding, GridLandReaction } from "./land/GridLandReaction";
import { NULL_LOG, type Log } from "../../log/Log";
import type { Container } from "pixi.js";
import { SymbolView } from "./SymbolView";
import type { GridModel } from "../../grid/GridModel";
import type { SymbolDefinitionResolver } from "../../symbols/SymbolDefinition";
import { GridLayout, type ComputedGridLayout, type GridPoint } from "./GridLayout";
import { GridSymbolPool, type SymbolBindOpts, type SymbolViewFactory } from "./GridSymbolPool";

/** The grid's audio surface: one-shots (optionally delayed on engine time) and the reel roll loops. */
export interface GridAudioSoundPlayer {
  playSoundId: (soundId: string, opts?: { playbackRate?: number }) => void;
  playSoundIdDelayed?: (soundId: string, delaySeconds: number, opts?: { playbackRate?: number }) => void;
  startLoop?: (soundId: string) => void;
  stopLoop?: (soundId: string) => void;
  setLoopGainScale?: (soundId: string, scale: number) => void;
}

/** Geometry of the grid: cell counts and size, the mount nodes, paddings, and the fit policy. */
export interface GridLayerOptions {
  gridColumnCount?: number;
  gridRowCount?: number;
  cellWidth?: number;
  cellHeight?: number;
  /** The node the mechanics scale and nudge (anticipation zoom, land bounce). */
  layoutRoot?: Container | null;
  /** The area the grid fits into when `autoFitToLayoutRoot` is on. */
  layoutArea?: { width: number; height: number } | null;
  autoFitToLayoutRoot?: boolean;
  gridRoot: Container;
  paddingLeft?: number;
  paddingRight?: number;
  paddingTop?: number;
  paddingBottom?: number;
  columnPaddingInner?: number;
  columnPaddingMiddle?: number;
  columnPaddingOuter?: number;
  /** Explicit per-column x offsets (design units); omitted → the layout's default even spacing. */
  columnOffsets?: number[];
  centerGrid?: boolean;
}

/** Everything the grid layer needs besides its geometry, given once at construction. */
export interface GridLayerDeps {
  definitionResolver: SymbolDefinitionResolver;
  log?: Log;
  /** Custom cell view factory; default `new SymbolView()`. */
  symbolFactory?: SymbolViewFactory | null;
  /** Called when a cell view is bound to a symbol id (effects attach here). */
  onSymbolBound?: (view: SymbolView, symbolId: string, opts?: SymbolBindOpts) => void;
  /** Called when a cell view returns to the pool (effects detach here). */
  onSymbolReleased?: (view: SymbolView) => void;
  /** What reacts to a column landing (sounds, a bounce, …), in dispatch order. */
  landReactions?: readonly GridLandReaction[];
}

/**
 * The read-only view of the grid's cells that effects and presenters take: where a cell is and what
 * sits in it. Nothing here can move a symbol.
 */
export interface GridCells {
  readonly gridColumnCount: number;
  readonly gridRowCount: number;
  /** The container the cells live in (their coordinate space for overlay placement). */
  readonly gridRoot: Container | null;
  getSymbolViewAtIndex(index: number): SymbolView | null;
  getCellLocalPosition(positionIndex: number): GridPoint;
  maskToIndices(mask: string): number[];
}

/** The cell map as `GravityDrop` sees it while it re-maps survivors and spawns. */
export interface CellRegistry {
  cellAt(index: number): SymbolView | undefined;
  adopt(index: number, view: SymbolView): void;
  forgetAll(): void;
}

/**
 * The grid: cells, the symbol pool, the layout, rendering, reset and the landing dispatch — and
 * nothing about how symbols MOVE. Mechanics (reel spin, tumble, cascade) hold a `GridLayer` and
 * drive it through the named operations below; the layer keeps map and pool consistent so no
 * mechanic has to (plan step 5, pass 3).
 */
export class GridLayer implements GridCells, CellRegistry {
  public gridColumnCount: number = 6;
  public gridRowCount: number = 5;
  public cellWidth: number = 120;
  public cellHeight: number = 120;
  public layoutRoot: Container | null = null;
  public layoutArea: { width: number; height: number } | null = null;
  public autoFitToLayoutRoot: boolean = true;
  public gridRoot: Container | null = null;
  public paddingLeft: number = 0;
  public paddingRight: number = 0;
  public paddingTop: number = 0;
  public paddingBottom: number = 0;
  public columnPaddingInner: number = 0;
  public columnPaddingMiddle: number = 0;
  public columnPaddingOuter: number = 0;
  public columnOffsets: number[] | undefined = undefined;
  public centerGrid: boolean = true;

  /** The layout maths (index ↔ row/col, local positions); shared with the motion classes the mechanics drive (`ReelDrum`, `GravityDrop`, `GridReveal`, `GridPreSpinDrop`). */
  public readonly layoutUtil = new GridLayout();
  private readonly symbolsByIndex: Map<number, SymbolView> = new Map();
  /** The symbol view pool; the drum, the drop and the reveal acquire the views they move from it. */
  public readonly pool = new GridSymbolPool(this.symbolsByIndex);

  private readonly symbolFactory: SymbolViewFactory | null;
  private readonly definitionResolver: SymbolDefinitionResolver;
  private readonly log: Log;
  private readonly landReactions: readonly GridLandReaction[];
  private computedLayout: ComputedGridLayout = { cellWidth: 120, cellHeight: 120, originX: 0, originY: 0, columnOffsets: [] };
  private lastRenderedModel: GridModel | null = null;

  public constructor(options: GridLayerOptions, deps: GridLayerDeps) {
    this.applyLayoutOptions(options);
    this.definitionResolver = deps.definitionResolver;
    this.log = deps.log ?? NULL_LOG;
    this.symbolFactory = deps.symbolFactory ?? null;
    if (deps.onSymbolBound) this.pool.setSymbolIdHook(deps.onSymbolBound);
    if (deps.onSymbolReleased) this.pool.setReleaseHook(deps.onSymbolReleased);
    this.landReactions = deps.landReactions ?? [];
  }

  public applyLayoutOptions(options: GridLayerOptions): void {
    this.gridRoot = options.gridRoot;
    if (options.gridColumnCount !== undefined) this.gridColumnCount = options.gridColumnCount;
    if (options.gridRowCount !== undefined) this.gridRowCount = options.gridRowCount;
    if (options.cellWidth !== undefined) this.cellWidth = options.cellWidth;
    if (options.cellHeight !== undefined) this.cellHeight = options.cellHeight;
    if (options.layoutRoot !== undefined) this.layoutRoot = options.layoutRoot;
    if (options.layoutArea !== undefined) this.layoutArea = options.layoutArea;
    if (options.autoFitToLayoutRoot !== undefined) this.autoFitToLayoutRoot = options.autoFitToLayoutRoot;
    if (options.paddingLeft !== undefined) this.paddingLeft = options.paddingLeft;
    if (options.paddingRight !== undefined) this.paddingRight = options.paddingRight;
    if (options.paddingTop !== undefined) this.paddingTop = options.paddingTop;
    if (options.paddingBottom !== undefined) this.paddingBottom = options.paddingBottom;
    if (options.columnPaddingInner !== undefined) this.columnPaddingInner = options.columnPaddingInner;
    if (options.columnPaddingMiddle !== undefined) this.columnPaddingMiddle = options.columnPaddingMiddle;
    if (options.columnPaddingOuter !== undefined) this.columnPaddingOuter = options.columnPaddingOuter;
    if (options.columnOffsets !== undefined) this.columnOffsets = options.columnOffsets;
    if (options.centerGrid !== undefined) this.centerGrid = options.centerGrid;
  }

  // ── Layout ────────────────────────────────────────────────────────────────────────────────

  /** Recomputes the layout from the current geometry and configures the pool with it. */
  public ensureLayout(): ComputedGridLayout {
    const layoutArea = this.layoutArea;
    this.computedLayout = this.layoutUtil.recalculate({
      gridColumnCount: this.gridColumnCount,
      gridRowCount: this.gridRowCount,
      cellWidth: this.cellWidth,
      cellHeight: this.cellHeight,
      layoutArea,
      autoFitToLayoutRoot: this.autoFitToLayoutRoot,
      paddingLeft: this.paddingLeft,
      paddingRight: this.paddingRight,
      paddingTop: this.paddingTop,
      paddingBottom: this.paddingBottom,
      columnPaddingInner: this.columnPaddingInner,
      columnPaddingMiddle: this.columnPaddingMiddle,
      columnPaddingOuter: this.columnPaddingOuter,
      columnOffsets: this.columnOffsets,
      centerGrid: this.centerGrid,
    });
    if (this.gridRoot === null) {
      throw new Error(
        "GridLayer: gridRoot must be bound. It is the parent container for spawned symbol cells " +
          "(the GridRoot node inside the grid container, under the grid mask).",
      );
    }
    this.pool.configure({ symbolFactory: this.symbolFactory, parentNode: this.gridRoot, log: this.log, resolver: this.definitionResolver, layout: this.computedLayout });
    return this.computedLayout;
  }

  public getComputedLayout(): ComputedGridLayout {
    return this.computedLayout;
  }

  public indexOf(row: number, col: number): number {
    return this.layoutUtil.rowColToIndex(this.gridColumnCount, row, col);
  }

  public getCellLocalPosition(positionIndex: number): GridPoint {
    this.ensureLayout();
    const row = Math.floor(positionIndex / this.gridColumnCount);
    const col = positionIndex % this.gridColumnCount;
    return this.layoutUtil.computeLocalPosition(this.computedLayout, row, col);
  }

  // ── Rendering ─────────────────────────────────────────────────────────────────────────────

  /**
   * Paint a whole model. With `preserveUnchanged` a cell that already shows the right symbol is
   * kept as it is (and keeps whatever it is animating) instead of being re-bound.
   */
  public renderGridModel(model: GridModel, options?: { preserveUnchanged?: boolean }): void {
    if (model.columnCount !== this.gridColumnCount || model.rowCount !== this.gridRowCount) {
      throw new Error(
        `GridLayer.renderGridModel size mismatch. Layer is ${this.gridColumnCount}x${this.gridRowCount}, model is ${model.columnCount}x${model.rowCount}.`,
      );
    }
    const symbolIds = model.cells.map((row) => row.map((cell) => cell.symbolId));
    this.renderFrame(symbolIds, options);
    this.lastRenderedModel = model;
  }

  public renderFrame(symbolIds: string[][], options?: { preserveUnchanged?: boolean }): void {
    this.ensureLayout();
    this.assertMatrix(symbolIds);
    const preserveUnchanged = options?.preserveUnchanged === true;
    if (preserveUnchanged) {
      for (const [index, view] of Array.from(this.symbolsByIndex.entries())) {
        if (this.isCellAlreadyRendered(index, view, symbolIds)) continue;
        this.pool.release(view, "render_frame_replace");
        this.symbolsByIndex.delete(index);
      }
    } else {
      this.pool.releaseAllCurrent();
    }
    for (let r = 0; r < this.gridRowCount; r++) {
      for (let c = 0; c < this.gridColumnCount; c++) {
        const index = r * this.gridColumnCount + c;
        const kept = preserveUnchanged ? this.symbolsByIndex.get(index) : undefined;
        if (kept && this.isCellAlreadyRendered(index, kept, symbolIds)) {
          const pos = this.layoutUtil.computeLocalPosition(this.computedLayout, r, c);
          kept.node.position.set(pos.x, pos.y);
          kept.node.visible = true;
          continue;
        }
        this.renderCell(index, symbolIds[r][c]);
      }
    }
  }

  private isCellAlreadyRendered(index: number, view: SymbolView | undefined, symbolIds: string[][]): boolean {
    if (!view || view.node.destroyed) return false;
    const r = Math.floor(index / this.gridColumnCount);
    const c = index % this.gridColumnCount;
    return view.getSymbolId() === symbolIds[r]?.[c];
  }

  /** Bind (or rebind) one cell to `symbolId` at its layout position. */
  public renderCell(index: number, symbolId: string): void {
    const r = Math.floor(index / this.gridColumnCount);
    const c = index % this.gridColumnCount;
    const view = this.pool.getOrCreate(index);
    this.pool.applySymbolIdToView(view, symbolId);
    const pos = this.layoutUtil.computeLocalPosition(this.computedLayout, r, c);
    view.node.position.set(pos.x, pos.y);
    view.node.visible = true;
  }

  /**
   * Release every cell to the pool and forget the rendered model (grid recovery). A cell the pool
   * neither pools nor has in use throws through (plan §2.6): the cell map is out of step with the pool.
   */
  public resetPresentationState(): void {
    this.ensureLayout();
    for (const view of new Set(this.symbolsByIndex.values())) this.pool.release(view, "grid_recovery_reset");
    this.symbolsByIndex.clear();
    this.lastRenderedModel = null;
  }

  /** The model last painted or committed by a mechanic; null after a reset. */
  public get lastModel(): GridModel | null {
    return this.lastRenderedModel;
  }

  /** A mechanic finished moving cells to `model` without a repaint (tumble, cascade); null: it emptied the grid (a drop-out). */
  public commitModel(model: GridModel | null): void {
    this.lastRenderedModel = model;
  }

  // ── Cells (the mechanics' named operations) ───────────────────────────────────────────────

  public getSymbolViewAtIndex(index: number): SymbolView | null {
    return this.symbolsByIndex.get(index) ?? null;
  }

  public cellAt(index: number): SymbolView | undefined {
    return this.symbolsByIndex.get(index);
  }

  /** Every bound cell view (a view may be bound at one index only). */
  public views(): IterableIterator<SymbolView> {
    return this.symbolsByIndex.values();
  }

  /** A copy of the index → view mapping (tumble plans carry one). */
  public cellSnapshot(): Map<number, SymbolView> {
    return new Map(this.symbolsByIndex);
  }

  /** Bind an existing view (a landed lane, a dropped survivor) as the cell at `index`. */
  public adopt(index: number, view: SymbolView): void {
    this.symbolsByIndex.set(index, view);
  }

  /** Return the cell at `index` to the pool and forget it; a no-op for an empty index. */
  public releaseAt(index: number, reason: string): void {
    const view = this.symbolsByIndex.get(index);
    if (!view) return;
    this.pool.release(view, reason);
    this.symbolsByIndex.delete(index);
  }

  /** Return a view that is NOT bound to a cell (a spare lane view) to the pool. */
  public releaseView(view: SymbolView, reason: string): void {
    this.pool.release(view, reason);
  }

  /** Forget every binding without releasing (a mechanic re-maps the same views right after). */
  public forgetAll(): void {
    this.symbolsByIndex.clear();
  }

  // ── Masks ─────────────────────────────────────────────────────────────────────────────────

  public maskToIndices(mask: string): number[] {
    this.assertMask(mask);
    const out: number[] = [];
    for (let i = 0; i < mask.length; i++) if (mask[i] === "1") out.push(i);
    return out;
  }

  public assertMask(mask: string): void {
    const expected = this.gridColumnCount * this.gridRowCount;
    if (mask.length !== expected) {
      throw new Error(`GridLayer mask length must be ${expected}, got ${mask.length}.`);
    }
  }

  private assertMatrix(symbolIds: string[][]): void {
    if (symbolIds.length !== this.gridRowCount) {
      throw new Error(`GridLayer.renderFrame expected ${this.gridRowCount} rows, got ${symbolIds.length}.`);
    }
    for (const row of symbolIds) {
      if (row.length !== this.gridColumnCount) {
        throw new Error(`GridLayer.renderFrame expected ${this.gridColumnCount} cols per row, got ${row.length}.`);
      }
    }
  }

  // ── Landings ──────────────────────────────────────────────────────────────────────────────

  /** A new spin / tumble step: reactions reset their per-sequence state. */
  public notifySequenceStart(): void {
    for (const reaction of this.landReactions) reaction.onSequenceStart?.();
  }

  /**
   * A column landed. `symbolIds` are the column's finals on the reel path (the grid is index-bound
   * only after every reel stops); without them the bound views are scanned, skipping cells that
   * already reacted to a landing (a held symbol across re-scans). Dispatches to every reaction in
   * order, then marks the scanned views.
   */
  public notifyColumnLanded(column: number, options?: { symbolIds?: readonly string[]; isChainDrop?: boolean }): void {
    const views: SymbolView[] = [];
    let symbolIds: readonly string[];
    if (options?.symbolIds) {
      symbolIds = options.symbolIds;
    } else {
      const ids: string[] = [];
      for (let row = 0; row < this.gridRowCount; row++) {
        const view = this.getSymbolViewAtIndex(this.indexOf(row, column));
        if (!view || view.hasPlayedLandSound()) continue;
        views.push(view);
        ids.push(view.getSymbolId() ?? "");
      }
      symbolIds = ids;
    }
    const landing: ColumnLanding = {
      column,
      symbolIds,
      definitions: symbolIds.map((id) => this.pool.resolveDefinition(id)),
      views,
      isChainDrop: options?.isChainDrop === true,
    };
    for (const reaction of this.landReactions) reaction.onColumnLanded(landing);
    for (const view of views) view.markLandSoundPlayed();
  }
}
